import { Cause, Effect, Layer, Result, Scope } from "effect";
import { modalFocusFailure } from "./modal-focus-model.ts";
import { ModalFocusIo } from "./modal-focus-service.ts";

const FOCUSABLE = ["a[href]", "button:not([disabled])", "input:not([disabled])", "select:not([disabled])", "textarea:not([disabled])", "[contenteditable='true']", "[tabindex]:not([tabindex='-1'])"].join(",");
export const ModalFocusIoLive = Layer.succeed(ModalFocusIo, ModalFocusIo.of({
  watch: (input, canRead, report) => Effect.gen(function* () {
    const owner = yield* Scope.Scope;
    const alive = () => canRead() && owner.state._tag !== "Closed";
    if (!alive()) return yield* Effect.interrupt;
    const page = yield* Effect.try({ try: () => typeof document === "undefined" ? undefined : document, catch: modalFocusFailure });
    if (!page) return;
    const remember = (event?: FocusEvent) => {
      if (!alive()) return;
      const result = Result.try(() => {
        if (input.isOpen()) return;
        const target = event?.target ?? page.activeElement;
        if (target instanceof HTMLElement) input.remember(target);
      });
      if (Result.isFailure(result)) report(modalFocusFailure(result.failure));
    };
    yield* Effect.addFinalizer(() => Effect.try({ try: () => page.removeEventListener("focusin", remember), catch: modalFocusFailure }).pipe(Effect.orDie));
    yield* Effect.sync(() => remember());
    if (!alive()) return yield* Effect.interrupt;
    yield* Effect.try({ try: () => page.addEventListener("focusin", remember), catch: modalFocusFailure });
  }),
  trap: (dialog, input, canRead, report) => Effect.gen(function* () {
    const owner = yield* Scope.Scope;
    const alive = () => canRead() && owner.state._tag !== "Closed";
    if (!alive()) return yield* Effect.interrupt;
    const page = yield* Effect.try({ try: () => typeof document === "undefined" ? undefined : document, catch: modalFocusFailure });
    if (!page) return;
    const previous = yield* Effect.try({ try: input.previous, catch: modalFocusFailure });
    const focusable = () => dialog ? [...dialog.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(element => {
      const style = window.getComputedStyle(element);
      return !element.hidden && element.getAttribute("aria-hidden") !== "true" && style.display !== "none" && style.visibility !== "hidden";
    }) : [];
    const focusFirst = () => (dialog?.querySelector<HTMLElement>("[autofocus]") ?? focusable()[0] ?? dialog)?.focus();
    const guarded = (operation: () => void) => {
      if (!alive()) return;
      const result = Result.try(operation);
      if (Result.isFailure(result)) report(modalFocusFailure(result.failure));
    };
    const onKeyDown = (event: KeyboardEvent) => guarded(() => {
      if (event.key === "Escape") { event.preventDefault();input.close();return; }
      if (event.key !== "Tab" || !dialog) return;
      const entries = focusable();
      if (entries.length === 0) { event.preventDefault();dialog.focus();return; }
      const first = entries[0], last = entries.at(-1), active = page.activeElement;
      if (!first || !last) return;
      if (event.shiftKey && (active === first || active === dialog || !dialog.contains(active))) { event.preventDefault();last.focus(); }
      else if (!event.shiftKey && (active === last || active === dialog || !dialog.contains(active))) { event.preventDefault();first.focus(); }
    });
    const onFocusIn = (event: FocusEvent) => guarded(() => { if (!dialog?.contains(event.target as Node | null)) focusFirst(); });
    yield* Effect.addFinalizer(() => Effect.gen(function* () {
      const releases = [
        Effect.try({ try: () => page.removeEventListener("keydown", onKeyDown), catch: modalFocusFailure }),
        Effect.try({ try: () => page.removeEventListener("focusin", onFocusIn), catch: modalFocusFailure }),
        Effect.try({ try: () => { if (previous?.isConnected) previous.focus(); }, catch: modalFocusFailure })
      ];
      const results = yield* Effect.forEach(releases, Effect.exit);
      const reasons = results.flatMap(result => result._tag === "Failure" ? result.cause.reasons : []);
      if (reasons.length) return yield* Effect.failCause(Cause.fromReasons(reasons));
    }).pipe(Effect.orDie));
    if (!alive()) return yield* Effect.interrupt;
    yield* Effect.try({ try: focusFirst, catch: modalFocusFailure });
    yield* Effect.try({ try: () => page.addEventListener("keydown", onKeyDown), catch: modalFocusFailure });
    yield* Effect.try({ try: () => page.addEventListener("focusin", onFocusIn), catch: modalFocusFailure });
  })
}));
